import { Box, Button, IconButton, Modal } from "@mui/material";
import React, { ChangeEvent, useState } from "react";
import IconifyIcon from "../../IconifyIcon";
import {
  useAppDispatch,
  useAppSelector,
  useIsMobile,
} from "../../../app/hooks";
import { selectLabels } from "../../../slices/language";
import { selectUser, selectUserRole, setUser } from "../../../slices/user";
import "./user-modal.css";
import { transform } from "typescript";
import ManageAccountModal from "../ManageAccountModal";
import api from "../../../api";
import { showError } from "../../../utils/helpers";
import { selectToken } from "../../../slices/token";

interface UserModalProps {
  showUserDetails: boolean;
  setShowUserDetails: (showUserDetails: boolean) => void;
  onLogout: () => void;
}

const UserModal = ({
  showUserDetails,
  setShowUserDetails,
  onLogout,
}: UserModalProps) => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);

  const [showManageModal, setShowManageModal] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = React.useState<string | ArrayBuffer | null>(
    null
  );
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [resetKey, setResetKey] = React.useState(0);

  const userImage = user
    ? user?.image?.length > 0
      ? user?.image[0]?.url
      : ""
    : "";

  const style = {
    position: "absolute" as "absolute",
    boxSizing: "border-box",
    minWidth: isMobile ? 330 : 350,
    top: isMobile ? 50 : 60,
    right: isMobile ? 12 : 16,
    overflow: "auto",
    borderRadius: "20px",
    bgcolor: "#f0f0f0",
    boxShadow: 3,
    outline: 0,
    border: "1px #dfdfdf solid",
    p: 2,
  };

  const userInitial = user?.name ? user?.name.toUpperCase().charAt(0) : "";
  const userRoleValue =
    userRole === "ciso"
      ? labels.ciso
      : userRole === "admin"
      ? labels.property_manager
      : userRole === "secondary_host"
      ? labels.secondary_user
      : userRole === "host"
      ? labels.main_user
      : labels.security_guard;

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const onSummitFunc = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    if (e.target.files[0]) {
      const eventFile = e.target.files[0];
      if (eventFile) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageSrc(reader.result);
        };

        setImageFile(eventFile);

        reader.readAsDataURL(eventFile);

        uploadImage(eventFile);
      }
    }
    setResetKey((prevKey) => prevKey + 1);
  };

  const uploadImage = async (file: File) => {
    if (user && token) {
      let imageId: number = 0;
      if (user?.image && user?.image.length > 0) {
        imageId = user?.image[0]?.id;
      }
      try {
        const uploadUserImage = await api.uploadMediaRequest({
          files: [file],
          ref: "plugin::users-permissions.user",
          refId: user?.id,
          field: "image",
        });
        if (uploadUserImage) {
          if (imageId > 0) {
            await api.deleteMediaRequest(imageId);
          }
          const response = await api.userByIdRequest(user?.id, token);
          if (response) {
            dispatch(setUser(response));
          }
        }
      } catch (error: any) {
        console.error(error);
        showError(dispatch, labels.generic_error);
      }
    }
  };

  return (
    <Modal
      open={showUserDetails}
      onClose={() => setShowUserDetails(false)}
      slotProps={{ backdrop: { style: { backgroundColor: "transparent" } } }}
    >
      <Box sx={style}>
        <ManageAccountModal
          showModal={showManageModal}
          setShowModal={setShowManageModal}
          user={user}
        />
        <div className="w-100 flex flex-dir-col align-items-center border-box">
          <div className="w-100 flex align-items-center justify-content-between border-box px-6">
            <div style={{ width: 22 }} />
            <p
              className="body-text-md font-w-500"
              style={{ textAlign: "center" }}
            >
              {user?.name}
            </p>
            <div style={{ width: 22 }}>
              <IconButton onClick={() => setShowUserDetails(false)}>
                <IconifyIcon icon={"icon-park-outline:close"} />
              </IconButton>
            </div>
          </div>
          <p className="body-text-xs mt-3 uppercase">{user?.company?.name}</p>
          {user?.property?.name && (
            <p className="body-text-xs mt-3">{user?.property?.name}</p>
          )}
          <p className="body-text-xs mt-3">{userRoleValue}</p>
          <div className="user-photo-container mt-10">
            <div
              className="user-photo-container"
              style={{ overflow: "hidden" }}
              onClick={handleUploadClick}
            >
              {userImage ? (
                <img
                  src={userImage}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <p style={{ color: "white", fontWeight: 400, fontSize: 40 }}>
                  {userInitial}
                </p>
              )}
            </div>
            <div className="edit-user-photo">
              <IconifyIcon
                icon={"ic:round-edit"}
                style={{ fontSize: 16, color: "#8a8c91" }}
              />
            </div>
          </div>
          <Button
            style={{ width: 200, fontSize: 12 }}
            className="mt-10"
            variant="contained"
            disableElevation
            onClick={() => setShowManageModal(true)}
          >
            {labels.manage_your_account}
          </Button>
          <Button
            style={{ width: 200, fontSize: 12 }}
            className="mt-6"
            variant="text"
            disableElevation
            startIcon={<IconifyIcon icon={"material-symbols:logout"} />}
            onClick={onLogout}
          >
            {labels.log_out}
          </Button>
          <div className="w-100 flex align-items-center justify-content-center">
            <p className="link-text mt-12">{labels.privacy_policy}</p>
          </div>
        </div>
        <input
          ref={inputRef}
          key={resetKey}
          type="file"
          accept=".jpg, .jpeg, .png, .gif, .pdf"
          onChange={(e) => onSummitFunc(e)}
          style={{ display: "none" }}
          id="image-input"
        />
      </Box>
    </Modal>
  );
};

export default UserModal;
